import { from } from 'rxjs'
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators'
import { errorNotification } from '~/Services'
import { Client, ClientsService } from '~/Services/Clients'
import { RootEpic } from '~/Store/types'
import { ClientActions } from './slice'

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback

const getClientsEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ClientActions.getClientsRequest.match),
    switchMap(action => {
      const { onSuccess, onFailure } = action.payload || {}

      return from(ClientsService.Get.getAllClients()).pipe(
        map(res => {
          const data = (res as any).data
          const clients = Array.isArray(data) ? data : data?.results || []
          return ClientActions.getClientsSuccess(clients)
        }),
        tap(resultAction => {
          if (onSuccess) {
            onSuccess(resultAction.payload)
          }
        }),
        catchError(error => {
          errorNotification(error)
          const message = error?.message || 'Failed to fetch clients'
          if (onFailure) {
            onFailure(message)
          }
          return [ClientActions.getClientsFailure(message)]
        }),
      )
    }),
  )

const getClientByIdEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ClientActions.getClientByIdRequest.match),
    switchMap(action => {
      const { clientId, onSuccess, onFailure } = action.payload

      return from(ClientsService.Get.getClientById(clientId)).pipe(
        map(res => ClientActions.getClientByIdSuccess((res as any).data)),
        tap(resultAction => {
          if (onSuccess) {
            onSuccess(resultAction.payload)
          }
        }),
        catchError(error => {
          errorNotification(error)
          const message = error?.message || 'Failed to fetch client detail'
          if (onFailure) {
            onFailure(message)
          }
          return [ClientActions.getClientByIdFailure(message)]
        }),
      )
    }),
  )

const createClientEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ClientActions.createClientRequest.match),
    switchMap(action => {
      const {
        data,
        onSuccess,
        onFailure,
      } = action.payload

      return from(ClientsService.Post.createNewClient(data)).pipe(
        switchMap(async res => {
          const client = (res as any).data as Client

          try {
            await ClientsService.Post.createNewProduct(
              client.clientName || data.clientName,
              client.clientId,
            )

            if (onSuccess) {
              onSuccess(client)
            }

            return [
              ClientActions.createClientSuccess(client),
              ClientActions.getClientsRequest(),
            ]
          } catch (error) {
            errorNotification(error)
            const message = `Client created, but product creation failed: ${getErrorMessage(
              error,
              'Failed to create product',
            )}`

            if (onFailure) {
              onFailure(message)
            }

            return [
              ClientActions.createClientFailure(message),
              ClientActions.getClientsRequest(),
            ]
          }
        }),
        switchMap(result => from(result)),
        catchError(error => {
          errorNotification(error)
          const message = getErrorMessage(error, 'Failed to create client')
          if (onFailure) {
            onFailure(message)
          }
          return [
            ClientActions.createClientFailure(message),
            ClientActions.getClientsRequest(),
          ]
        }),
      )
    }),
  )

const updateClientEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ClientActions.updateClientRequest.match),
    switchMap(action => {
      const { clientId, data, onSuccess, onFailure } = action.payload

      return from(ClientsService.Put.updateClientInfo(clientId, data)).pipe(
        map(res => ClientActions.updateClientSuccess((res as any).data)),
        tap(resultAction => {
          if (onSuccess) {
            onSuccess(resultAction.payload)
          }
        }),
        catchError(error => {
          errorNotification(error)
          const message = error?.message || 'Failed to update client'
          if (onFailure) {
            onFailure(message)
          }
          return [ClientActions.updateClientFailure(message)]
        }),
      )
    }),
  )

export const ClientEpics = [
  getClientsEpic,
  getClientByIdEpic,
  createClientEpic,
  updateClientEpic,
]
